import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';

import { ConfigService } from '@ngx-config/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { sprintf } from 'sprintf-js';
import { isArray } from 'util';
import * as moment from 'moment';
import 'moment-timezone';
import { OAuthService } from 'angular-oauth2-oidc';
const md5 = require('md5');

import { Webstart, WebstartList, WebstartResponse } from './models/webstart-response';
import { getSelectedAgency, State } from '../state-management/reducers';
import { Agency } from '../services/agencies/agencies.model';


@Injectable()
export class WebstartService implements Resolve<WebstartResponse> {
	private cache: WebstartResponse = {};

	private static getHash(userId: string): string {
		return md5(`${moment().tz('Europe/Berlin').format('MMYY')}~aLOkne1AlOnE~${userId}~*`);
	}

	public isPending$ = new BehaviorSubject<boolean>(false);

	constructor(
		private http: HttpClient,
		private config: ConfigService,
		private oauthService: OAuthService,
		private _store: Store<State>
	) {}

	public getWebstartByIdentifier(webstartIdentifier: string): Observable<WebstartList|null> {
		const claims = this.oauthService.getIdentityClaims();

		if (!claims) {
			return Observable.of(null);
		}

		const USER_ID = claims['sub'];

		const url = sprintf(
			this.config.getSettings('webstart', 'endpoint').endpoint,
			{user_id: USER_ID, identifier: webstartIdentifier, hash: WebstartService.getHash(USER_ID)}
		);

		this.isPending$.next(true);

		return this.http.get<WebstartResponse>(`${url}?options=1`)
			.map(res => res[webstartIdentifier])
			.do(res => {
				this.cache[webstartIdentifier] = res;
				this.isPending$.next(false);
			})
			.catch((error: HttpErrorResponse) => Observable.throw(error))
			.publishReplay(1).refCount();
	}

	public getAvailableWebstarts(): Observable<WebstartResponse> {
		const claims = this.oauthService.getIdentityClaims();

		if (!claims) {
			return Observable.of(null);
		}

		const USER_ID = claims['sub'];

		const url = sprintf(
			this.config.getSettings('webstart', 'endpoint').endpoint,
			{user_id: USER_ID, identifier: 'neo', hash: WebstartService.getHash(USER_ID)}
		);

		this.isPending$.next(true);
		return this.http.get<WebstartResponse>(`${url}?options=1`)
			.do(res => {
				this.cache = {...this.cache, ...res};
				this.isPending$.next(false);
			})
			.catch((error: HttpErrorResponse) => Observable.throw(error))
			.publishReplay(1).refCount();
	}

	public getCachedWebstartByIdentifier(webstartIdentifier: string): Observable<WebstartList|null> {
		if (this.cache && this.cache[webstartIdentifier]) {
			return Observable.of(this.cache[webstartIdentifier]);
		}

		return this.getWebstartByIdentifier(webstartIdentifier);
	}

	public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<WebstartResponse> {
		if (this.cache) {
			return Observable.of(this.cache);
		}

		return this.getAvailableWebstarts();
	}

	public getWebstartForSelectedAgent(identifier: string): Observable<Webstart|null> {
		return this._store.select(getSelectedAgency).take(1).switchMap(agency => {
			if (!agency) {
				return Observable.of(null);
			}

			if (this.cache && isArray(this.cache[identifier]) && this.cache[identifier].length > 0) {
				return Observable.of(this._findWebstartByAgent(this.cache[identifier], agency));
			}

			return this.getWebstartByIdentifier(identifier).map(webstarts => {
				if (!isArray(webstarts)) {
					return null;
				}

				return this._findWebstartByAgent(webstarts, agency);
			});
		});
	}

	public hasAgentCachedWebstart(webstartIdentifier: string): Observable<boolean> {
		return this.getWebstartForSelectedAgent(webstartIdentifier).take(1).map(webstart => webstart !== null);
	}

	private _findWebstartByAgent(webstarts: Webstart[], agency: Agency): Webstart|null {
		return webstarts.find(webstart => webstart.agentId.indexOf(`${agency.agentId}`) !== -1) || null;
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/webstart/webstart.service.ts